import { prefersReducedMotion } from '@scripts/lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import type { HeroRipple } from './hero-ripple';

// 波紋の中心(左上原点 uv)。縦は中央、横は 1/4 の地点に固定する
const RIPPLE_CENTER = { x: 1 / 4, y: 0.5 };

// ローディング画面 (hero-loader.ts) がヒーローを見せ切ってから start を呼ぶ。
// ローダーが無い (=想定外だが保険) 場合は即実行する。イントロとスライダーの
// 自動再生を「ヒーローが見えてから」始めるためのゲート
function onHeroRevealed(start: () => void) {
  if (document.querySelector('[data-hero-loader]')) {
    window.addEventListener('soak:hero-loader-done', start, { once: true });
  } else {
    start();
  }
}

export function initHero() {
  const hero = document.querySelector<HTMLElement>('[data-hero]');
  if (!hero) return;

  // ヒーローが画面上端に掛かっている間だけヘッダーをヒーロー上モードにする。
  // 視認性の切り替えなので reduced motion でも動かす。
  // IntersectionObserver では「ヒーロー下端 = 画面上端」でちょうど接している
  // 判定 (isIntersecting: true) になり、#products へのアンカースクロールの
  // 着地点がまさにその位置のため切り替わらない。閾値を数 px 手前に置ける
  // ScrollTrigger で判定する (lenis のスクロールにも同期している)
  const header = document.querySelector<HTMLElement>('[data-site-header]');
  if (header) {
    const setOnHero = (on: boolean) =>
      header.toggleAttribute('data-on-hero', on);
    const trigger = ScrollTrigger.create({
      trigger: hero,
      // ヒーロー下端が画面上端の 8px 手前に来た時点で通常モードへ。
      // スムーススクロールの着地誤差で取りこぼさないためのマージン
      start: 'bottom top+=8',
      end: 'max',
      onEnter: () => setOnHero(false),
      onLeaveBack: () => setOnHero(true),
      // リサイズ・リフレッシュ時に現在位置から状態を取り直す
      onRefresh: (self) => setOnHero(self.scroll() < self.start),
    });
    setOnHero(trigger.scroll() < trigger.start);
  }

  if (prefersReducedMotion) return;

  const lines = hero.querySelectorAll('[data-hero-line]');
  const tagline = hero.querySelector('[data-hero-tagline]');
  const hint = hero.querySelector('[data-hero-scroll-hint]');

  const nav = hero.querySelector('[data-hero-nav]');

  // 初期非表示は global.css の `html.js [data-hero-*]` が paint 前に伏せており、
  // ちらつきを防ぐ。CSS で既に伏せているため gsap.from は使えない (現在値を終点と
  // 見なし消えたままになる)。fromTo で終点を明示する。line=transform / それ以外=
  // autoAlpha と、CSS で伏せたプロパティに一致させる。paused で作り、ローディング
  // 画面が退いてヒーローが見えてから再生する (ローダーの裏で先行再生しない)
  const intro = gsap
    .timeline({ defaults: { ease: 'power3.out' }, paused: true })
    // CSS の translateY(110%) を gsap は px に解決して y へ読み込むため、yPercent
    // だけ戻しても px の y が残りずれたまま隠れる。y:0 を明示して打ち消す
    .fromTo(
      lines,
      { yPercent: 110, y: 0 },
      { yPercent: 0, y: 0, duration: 1.1, stagger: 0.14, delay: 0.2 },
    )
    .fromTo(
      tagline,
      { autoAlpha: 0, y: 12 },
      { autoAlpha: 1, y: 0, duration: 0.8 },
      '-=0.9',
    )
    .fromTo(hint, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.8 }, '<')
    .fromTo(nav, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.8 }, '<');

  onHeroRevealed(() => intro.play());

  // 背景写真の軽いパララックス。ぴったり配置(オーバースキャンなし)の間は
  // data-hero-bg を付けていないので動かない
  const bg = hero.querySelector('[data-hero-bg]');
  if (bg) {
    gsap.to(bg, {
      yPercent: 10,
      ease: 'none',
      scrollTrigger: {
        trigger: hero,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    });
  }
}

// ヒーロー背景写真のスライダー。ProductSection と同じ機構(クロスフェード +
// カウントダウン tween による自動再生)の単一スライダー版。ナビは矢印なし、
// 数字の代わりにストーリー型バー(再生中のバーが左→右へ白く満ちる)を使う
export function initHeroSlider() {
  const slider = document.querySelector<HTMLElement>('[data-hero-slider]');
  if (!slider) return;

  const slides = [...slider.querySelectorAll<HTMLElement>('[data-hero-slide]')];
  if (slides.length <= 1) return;

  const hero = document.querySelector<HTMLElement>('[data-hero]');
  const bars = [
    ...(hero?.querySelectorAll<HTMLElement>('[data-hero-bar]') ?? []),
  ];
  const fills = bars.map((bar) =>
    bar.querySelector<HTMLElement>('[data-hero-bar-fill]'),
  );
  const toggle = hero?.querySelector<HTMLElement>('[data-hero-toggle]');

  const AUTOPLAY_INTERVAL = 7000;
  let current = 0;
  let paused = false;
  // ヒーローが画面内に十分見えている間だけ自動再生する
  let visible = true;
  // ローディング画面が退いてヒーローが見えるまで自動再生を始めない。
  // WebGL の読み込み (下部 start()) はローダーの節目なので即時のままにし、
  // ここでは「カウントダウン開始」だけをゲートする
  let revealed = false;
  // WebGL リップル層。動的 import 後に準備完了で入る。null / 未 ready の間は
  // 従来の DOM クロスフェードで動く
  let ripple: HeroRipple | null = null;

  // バーの塗り。再生済み(< current)=満杯、未再生(> current)=0。
  // 再生中(= current)は進捗 p に追従して 0→1 へ満ちる。停止中は
  // カレントのバーを満杯にして「今どの写真か」を白く示す
  const renderBars = (p: number) => {
    fills.forEach((fill, i) => {
      if (!fill) return;
      const scale = i < current ? 1 : i === current ? (paused ? 1 : p) : 0;
      fill.style.transform = `scaleX(${scale})`;
    });
  };

  const goTo = (next: number, instant = false, center = RIPPLE_CENTER) => {
    if (next === current) return;
    const from = current;
    // 状態は即更新してバー/カウントダウンの整合を保つ
    current = next;

    if (instant || prefersReducedMotion) {
      gsap.set(slides[from], { opacity: 0 });
      gsap.set(slides[next], { opacity: 1 });
    } else if (ripple?.ready) {
      // 見た目の切替は WebGL の波紋。DOM slide も裏で同期しておき、
      // context-loss 時に canvas が透過へ戻っても正しい写真が透けるようにする
      gsap.set(slides[from], { opacity: 0 });
      gsap.set(slides[next], { opacity: 1 });
      ripple.transition(from, next, center);
    } else {
      // WebGL 未準備 / 失敗時: 従来の DOM クロスフェード
      gsap.to(slides[from], { opacity: 0, duration: 0.8 });
      gsap.to(slides[next], { opacity: 1, duration: 0.8 });
    }
  };

  // カウントダウンの進捗。トゥイーンで持つことで一時停止(位置保持)・再開・
  // リセットをバーの見た目と一体で扱える
  const progress = { value: 0 };
  const countdown = gsap.to(progress, {
    value: 1,
    duration: AUTOPLAY_INTERVAL / 1000,
    ease: 'none',
    paused: true,
    onUpdate: () => renderBars(progress.value),
    onComplete: () => {
      goTo((current + 1) % slides.length);
      renderBars(0);
      countdown.restart();
    },
  });

  const syncCountdown = () => {
    if (!paused && visible && revealed) countdown.play();
    else countdown.pause();
  };

  // 進捗を 0 に戻し、再生条件が揃っていれば最初から数え直す
  const resetCountdown = () => {
    countdown.pause(0);
    renderBars(0);
    syncCountdown();
  };

  const syncToggle = () => {
    if (!toggle) return;
    toggle.dataset.playing = paused ? 'false' : 'true';
    toggle.setAttribute(
      'aria-label',
      paused ? 'スライドショーを再生' : 'スライドショーを一時停止',
    );
  };

  const setPaused = (value: boolean) => {
    if (paused === value) return;
    paused = value;
    syncToggle();
    syncCountdown();
    // 停止/再生の切替でカレントバーの見た目(満杯↔進捗)を即反映する
    renderBars(progress.value);
  };

  toggle?.addEventListener('click', () => setPaused(!paused));

  // バークリックで該当スライドへジャンプ。自動再生は止め、進捗も空へ戻す
  bars.forEach((bar, j) =>
    bar.addEventListener('click', () => {
      setPaused(true);
      goTo(j);
      resetCountdown();
    }),
  );

  // 写真エリアのクリックで前後へ切替。右半分=次 / 左半分=前。着水点は
  // クリック位置にする。canvas は pointer-events-none、テキスト層が z-30 で
  // 最前面のため [data-hero] セクションへ委譲で付ける。ロゴ/タグライン/
  // 各コントロール(a・button)の上は写真エリア外として無視する
  hero?.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    if (target.closest('a, button, h1, [data-hero-tagline]')) return;

    const rect = slider.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    const next =
      x >= 0.5
        ? (current + 1) % slides.length
        : (current - 1 + slides.length) % slides.length;

    // バークリックと同様、手動操作なので自動再生を止め進捗を戻す
    setPaused(true);
    goTo(next, false, {
      x: Math.min(Math.max(x, 0), 1),
      y: Math.min(Math.max(y, 0), 1),
    });
    resetCountdown();
  });

  if (prefersReducedMotion) {
    // 静止表示。自動再生がなく一時停止ボタンは無意味なので隠す
    if (toggle) toggle.hidden = true;
    renderBars(0);
    return;
  }

  renderBars(0);

  // ヒーローが画面内に十分入っている間だけ自動再生する。スクロールで
  // 外れたら進捗を止めて写真位置は保持する
  const io = new IntersectionObserver(
    ([entry]) => {
      const nowVisible = entry.intersectionRatio >= 0.5;
      if (nowVisible === visible) return;
      visible = nowVisible;
      syncCountdown();
    },
    { threshold: [0, 0.5, 1] },
  );
  io.observe(slider);

  // ローディング画面が退いてヒーローが見えてから自動再生を始める
  onHeroRevealed(() => {
    revealed = true;
    syncCountdown();
  });

  // WebGL リップル層を遅延・ゲート付きで読み込む。LCP(<img fetchpriority>)を
  // 塞がないよう idle / load 後に動的 import する。準備完了で goTo が委譲を始める
  const canvas = slider.querySelector<HTMLCanvasElement>('[data-hero-canvas]');
  if (canvas) {
    // <picture> から desktop(img src)/ mobile(source srcset)の URL を取り出す。
    // 出し分けクエリも source の media から拾って Hero.astro と同期させる
    const slideData = slides.map((pic) => {
      const desktop = pic.querySelector('img')?.getAttribute('src') ?? '';
      const mobile =
        pic.querySelector('source')?.getAttribute('srcset') ?? desktop;
      return { desktop, mobile };
    });
    const mobileQuery =
      slider.querySelector('source')?.getAttribute('media') ??
      '(max-width: 1023px)';

    const start = () => {
      import('./hero-ripple')
        .then(({ initHeroRipple }) => {
          // three.js チャンク(約 563KB)の到着・評価完了。ローディング画面
          // (hero-loader.ts)の節目に使う
          window.dispatchEvent(new Event('soak:hero-ripple-loaded'));
          return initHeroRipple({
            canvas,
            slides: slideData,
            mobileQuery,
            initialIndex: current,
          });
        })
        .then((handle) => {
          ripple = handle;
          // テクスチャ読込・初回描画まで完了(ready)。ローディングの最終節目
          window.dispatchEvent(new Event('soak:hero-ripple-ready'));
          window.addEventListener('pagehide', () => handle.dispose(), {
            once: true,
          });
          if (import.meta.env.DEV) {
            import('./hero-ripple-dev-panel')
              .then((m) => m.initHeroRippleDevPanel(handle))
              .catch(() => {
                /* dev 専用パネルの失敗は無視 */
              });
          }
        })
        .catch((error) => {
          // 失敗時は canvas を残さず DOM クロスフェードに任せる
          console.error('Failed to init hero ripple:', error);
          canvas.remove();
          // ローディングが節目待ちで止まらないよう完了を通知(once で二重加算なし)
          window.dispatchEvent(new Event('soak:hero-ripple-loaded'));
          window.dispatchEvent(new Event('soak:hero-ripple-ready'));
        });
    };

    // ローディング画面を WebGL の読み込みに紐づけるため、idle 待ちにせず即時に
    // 動的 import を始める。ヒーローはローダーが覆っているので LCP は塞がない
    start();
  }
}
